import { ThresholdBar } from '../../../core/components/threshold-bar/ThresholdBar.js';
import { isInitialZoom } from '../../../core/components/zoom/utils/is-initial-zoom.js';
import { NumericalThresholdPosition } from '../../../core/types/thresholds.js';
import { transformThresholdsToMarkers } from '../../../core/utils/thresholds-to-markers.js';
import { useCurrentDomainState } from '../../hooks/store/selectors.js';
import { useSetState } from '../../hooks/store/state-setters.js';
import { useHistogramThresholds } from '../../hooks/useHistogramThresholds.js';
import { useHistogramYFormatter } from '../../hooks/useHistogramYFormatter.js';
import { useHistogramYScales } from '../../hooks/useHistogramYScales.js';
import { useHistogramZoomRanges } from '../../hooks/useHistogramZoomRanges.js';

interface HistogramThresholdBarProps {
  position: NumericalThresholdPosition;
  length: number;
}

export const HistogramThresholdBar = (props: HistogramThresholdBarProps) => {
  const { position, length } = props;

  const [leftScale, rightScale] = useHistogramYScales();
  const { leftThreshold, rightThreshold } = useHistogramThresholds();
  const currentDomain = useCurrentDomainState();
  const { initialZoom } = useHistogramZoomRanges();
  const isChartWithoutZoom =
    !currentDomain ||
    isInitialZoom(currentDomain, initialZoom as [number, number]);
  const [leftFormatter, rightFormatter] = useHistogramYFormatter();
  const setState = useSetState();

  const thresholds = position === 'left' ? leftThreshold : rightThreshold;
  if (!thresholds || !thresholds.length) {
    return null;
  }

  const yScale = position === 'left' ? leftScale : rightScale;
  const isFlipped = position !== 'left';
  const axisFormatter = position === 'left' ? leftFormatter : rightFormatter;

  const markers = transformThresholdsToMarkers(
    thresholds,
    position,
    yScale,
    axisFormatter,
  );

  const handleHover = (id: string | undefined) => {
    setState((prev) => ({
      ...prev,
      hoveredThresholdId: id,
    }));
  };

  return markers.length ? (
    <ThresholdBar
      markers={isChartWithoutZoom ? markers : []}
      length={length}
      layout="vertical"
      isFlipped={isFlipped}
      onHover={handleHover}
    />
  ) : null;
};

HistogramThresholdBar['displayName'] = 'HistogramThresholdBar';
